import * as React from 'react';
import QRCode from 'react-qr-code';
import BlocksLoaderIcon from '@/images/blocks-loader-icon.svg';
import { useDevice } from '@/hooks/shared/use-device';
import { PaymentStatus } from '@/types/api/payment/payment-status.enum';
import { usePaymentQuery } from '@/hooks/payment/queries/use-payment-query';
import { PayerRole } from '@/types/api/payment';
import { generateSwishUrl } from '@/utils/payment/payment';

const textMapping = {
  thisDevice: {
    message: { [PayerRole.User]: 'Klicka på knappen och betala dig med Swish.', [PayerRole.Customer]: '' },
    switchButton: 'Swish på annan enhet? Visa QR-kod istället.',
  },
  notThisDevice: {
    message: {
      [PayerRole.User]: 'Öppna Swish-appen på din mobil och scanna QR-koden.',
      [PayerRole.Customer]: 'Be din kund öppna Swish-appen på sin mobil och skanna QR-koden.',
    },
    switchButton: 'Swish på denna enhet?',
  },
};

export interface PaymentPendingModalProps {
  paymentId: string;
}

export default function PaymentPendingModal({ paymentId }: PaymentPendingModalProps) {
  const { data: payment } = usePaymentQuery(paymentId);
  const { isMobileOrTablet } = useDevice();
  const [swishOnThisDevice, setSwishOnThisDevice] = React.useState<boolean>(
    isMobileOrTablet && payment?.payerRole === PayerRole.User,
  );
  const text = textMapping[swishOnThisDevice ? 'thisDevice' : 'notThisDevice'];
  const swishUrl = generateSwishUrl(payment?.methodData?.swishToken!, paymentId);

  React.useEffect(() => {
    if (swishOnThisDevice) {
      window.location = swishUrl as any;
    }
  }, [swishUrl]);

  return (
    <>
      <div className="mb-4 text-center">
        <div className="text-lg font-semibold text-gray-800 dark:text-gray-100 mt-4">Inväntar betalning...</div>
      </div>

      {/* Modal content */}
      <div className="text-center">
        <div className="flex flex-col items-center">
          {(!payment || payment.status === PaymentStatus.Initiating) && (
            <div className="inline-flex my-10">
              <img src={BlocksLoaderIcon} alt="Loading" />
            </div>
          )}
          {payment && payment.status === PaymentStatus.Pending && (
            <>
              {swishOnThisDevice ? (
                <OpenSwishButton url={swishUrl} />
              ) : (
                <QRCode size={200} value={`D${payment.methodData.swishToken}`} className="mt-4 mb-8" />
              )}
              <div className="text-sm mb-2">{text.message[payment.payerRole]}</div>
              {payment.payerRole === PayerRole.User && (
                <div className="text-sm mb-5">
                  <button
                    onClick={() => setSwishOnThisDevice((prev) => !prev)}
                    type="button"
                    className="underline text-violet-500 hover:text-violet-800"
                  >
                    {text.switchButton}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}

const OpenSwishButton = ({ url }: { url: string }) => {
  return (
    <a
      href={url}
      className="btn-lg mt-4 mb-8 bg-gray-900 text-gray-100 hover:bg-gray-800 dark:bg-gray-100 dark:text-gray-800 dark:hover:bg-white"
    >
      Öppna Swish-appen
    </a>
  );
};
